import React, { useState, useRef, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../lib/api';
import StatusBadge from '../components/shared/StatusBadge';
import { CheckCircle2, XCircle, Truck, ArrowLeft, Copy, X } from 'lucide-react';
import { toast } from 'sonner';

export default function OrderDetail() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [courier, setCourier] = useState('Delhivery');
  const [tracking, setTracking] = useState('');
  const [couriers, setCouriers] = useState([]);
  const [customCourier, setCustomCourier] = useState('');
  const [parcelFile, setParcelFile] = useState(null);
  const parcelInputRef = useRef(null);
  const [shipping, setShipping] = useState(false);
  const [deciding, setDeciding] = useState(false);
  const [proofSrc, setProofSrc] = useState('');

  const load = () => api.get(`/orders/${id}`).then(({ data }) => setOrder(data));

  useEffect(() => {
  let objectUrl;

  if (!order?.payment?.screenshot_url) {
    setProofSrc('');
    return;
  }

  api.get(`/orders/${id}/payment-screenshot`, {
    responseType: 'blob',
  }).then(({ data }) => {
    objectUrl = URL.createObjectURL(data);
    setProofSrc(objectUrl);
  }).catch((err) => {
    console.error('Failed to load payment screenshot:', err);
    setProofSrc('');
  });

  return () => {
    if (objectUrl) URL.revokeObjectURL(objectUrl);
  };
}, [id, order?.payment?.screenshot_url]);
  useEffect(() => {
  load();

  api.get('/couriers')
    .then(({ data }) => {
  console.log('COURIERS API RESPONSE:', data);
  const items = data.items || [];
  setCouriers(items);
})
.catch((e) => console.error('COURIERS API ERROR:', e));
}, [id]);

  if (!order) return <div className="text-fab-text-muted">Loading...</div>;
  const fmt = (n) => '₹' + Number(n || 0).toLocaleString('en-IN');

  const simUpload = async () => { await api.post(`/orders/${id}/upload-payment`, {}); toast.success('Buyer payment screenshot received'); load(); };
  const confirm = async () => {
    await api.post(`/orders/${id}/confirm-payment`, { payment_status: 'VERIFIED', amount_received: order.total_amount });
    toast.success('Payment confirmed — buyer notified'); load();
  };
  const reject = async () => {
    await api.post(`/orders/${id}/reject-payment`, { reason: 'Amount / reference did not match' });
    toast.warning('Payment marked as not received — buyer notified'); load();
  };
  const ship = async () => {
    if (!tracking) { toast.error('Enter tracking ID'); return; }
    setShipping(true);
    try {
      if (courier === 'Other') {
  const name = customCourier.trim();

  if (!name) {
    toast.error('Enter transporter name');
    return;
  }

  try {
    await api.post('/couriers', { name });
    const { data: courierData } = await api.get('/couriers');
    setCouriers(courierData.items);
    setCourier(name);
    toast.success('Transporter saved for future use');
  } catch (e) {
    toast.error(e.response?.data?.detail || 'Could not save transporter');
    return;
  }
}
      const fd = new FormData();
      fd.append('courier_name', courier === 'Other' ? customCourier.trim() : courier);
      fd.append('tracking_id', tracking);
      if (parcelFile) fd.append('parcel_image', parcelFile);
      const { data } = await api.post(`/orders/${id}/ship`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (data.buyer_notified) {
        toast.success('Shipped — buyer notified on WhatsApp 📦');
      } else if (data.buyer_notify_error) {
        toast.warning('Shipped, but WhatsApp notify failed (see Order timeline)');
      } else {
        toast.success('Marked as shipped');
      }
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
toast.error(
  typeof detail === 'string'
    ? detail
    : Array.isArray(detail)
      ? detail.map((item) => item.msg || String(item)).join(', ')
      : detail?.msg || 'Ship failed'
);
    } finally { setShipping(false); }
  };
  const deliver = async () => { await api.post(`/orders/${id}/deliver`); toast.success('Marked as delivered'); load(); };

  // Phase 2 — Credit/COD approval + credit reminder (deterministic, zero AI).
  const pa = order.payment_approval || {};
  const ptPending = pa.status === 'PENDING' && (pa.term === 'CREDIT' || pa.term === 'COD');
  const ptApproved = pa.status === 'APPROVED' && (pa.term === 'CREDIT' || pa.term === 'COD');
  const decide = async (action) => {
    setDeciding(true);
    try {
      const { data } = await api.post(`/orders/${id}/payment-term-decision`, { action });
      toast.success(action === 'approve' ? `${pa.term === 'CREDIT' ? 'Credit' : 'COD'} approved — buyer notified` : `${pa.term === 'CREDIT' ? 'Credit' : 'COD'} rejected — order cancelled`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Action failed');
    } finally { setDeciding(false); }
  };
  const notifyBuyer = async () => {
    try {
      const { data } = await api.post(`/orders/${id}/notify-buyer-payment`);
      if (data.ok) toast.success('Payment reminder sent to buyer');
      else toast.warning(data.message || 'Reminder not sent');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send reminder');
    }
  };
  const canShip = order.status === 'CONFIRMED'
    || (ptApproved && !['SHIPPED', 'DELIVERED', 'CANCELLED', 'PACKED'].includes(order.status));

  return (
    <div className="space-y-5 fade-up max-w-6xl">
      <Link to="/dashboard/orders" className="text-[13px] text-fab-text-secondary flex items-center gap-1 font-semibold"><ArrowLeft className="w-4 h-4" /> All Orders</Link>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div className="lg:col-span-3 space-y-4">
          <div className="fab-card p-5">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <div className="font-display font-bold text-xl text-fab-text font-mono">{order.order_number}</div>
                <div className="text-[12px] text-fab-text-muted">{new Date(order.created_at).toLocaleString()}</div>
              </div>
              <StatusBadge status={order.status} />
            </div>
            <div className="mt-4 grid grid-cols-2 gap-4 text-[13px]">
              <div><div className="text-[11px] text-fab-text-muted uppercase tracking-wider">Buyer</div><div className="font-semibold text-fab-text">{order.buyer_name}</div><div className="text-fab-text-secondary">{order.buyer_phone}</div></div>
              <div><div className="text-[11px] text-fab-text-muted uppercase tracking-wider">Shipping</div><div className="text-fab-text-secondary text-[12px]">{order.shipping_address || '—'}</div></div>
            </div>
          </div>

          <div className="fab-card p-5">
            <div className="font-display font-bold text-fab-text mb-3">Items</div>
            <div className="space-y-2">
              {order.items.map((it, i) => (
                <div key={i} className="flex items-center gap-3 p-2 rounded-lg hover:bg-fab-elevated">
                  <img src={it.product_image} alt={it.product_code} className="w-12 h-12 rounded-lg object-cover" />
                  <div className="flex-1">
                    <div className="font-mono text-[12px] font-bold text-fab-text">{it.product_code}</div>
                    <div className="text-[11px] text-fab-text-muted">{it.quantity} × {fmt(it.unit_price)}</div>
                  </div>
                  <div className="font-semibold text-fab-text">{fmt(it.subtotal)}</div>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-4 border-t border-fab-primary-lighter space-y-1 text-[13px]">
              <div className="flex justify-between text-fab-text-secondary"><span>Subtotal</span><span>{fmt(order.subtotal)}</span></div>
              {order.discount_amount > 0 && (
                <div className="flex justify-between text-fab-text-secondary"><span>Discount ({order.discount_percent}%, seller approved)</span><span>−{fmt(order.discount_amount)}</span></div>
              )}
              <div className="flex justify-between text-fab-text-secondary"><span>GST (5%)</span><span>{fmt(order.gst_amount)}</span></div>
              <div className="flex justify-between font-display font-bold text-fab-text text-[15px] pt-2 border-t border-fab-primary-lighter"><span>Total</span><span>{fmt(order.total_amount)}</span></div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-2 space-y-4">
          <div className="fab-card p-5">
            <div className="font-display font-bold text-fab-text mb-3">Timeline</div>
            <div className="space-y-3">
              {(order.timeline || []).map((t, i) => (
                <div key={i} className="flex items-start gap-2">
                  <div className="w-2 h-2 rounded-full bg-fab-primary mt-2" />
                  <div className="flex-1">
                    <div className="text-[13px] font-semibold text-fab-text">{t.event}</div>
                    <div className="text-[11px] text-fab-text-muted">{new Date(t.at).toLocaleString()}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {ptPending && (
            <div className="fab-card p-5 border-2 border-fab-orange" data-testid="payment-term-approval-card">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="font-display font-bold text-fab-orange flex items-center gap-1.5">
                  ⚠️ {pa.term === 'CREDIT' ? 'Purchase on Credit' : 'COD Order'}
                </div>
                <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-fab-orange/10 text-fab-orange border border-fab-orange/20" data-testid="payment-term-badge">
                  {pa.term === 'CREDIT' ? 'Credit Requested' : 'COD Requested'}
                </span>
              </div>
              <div className="text-[11px] text-fab-text-muted mt-1">Buyer: <span className="text-fab-text">{order.buyer_name || order.buyer_phone}</span></div>
              <div className="text-[11px] text-fab-text-muted">Order: <span className="text-fab-text">{order.order_number}</span></div>
              <div className="mt-1 text-[12px] italic text-fab-text-secondary">"{pa.term === 'CREDIT' ? 'Purchase on credit' : 'COD order'}"</div>
              <div className="text-[13px] text-fab-text-secondary mt-2">Amount: <strong className="text-fab-text">{fmt(order.total_amount)}</strong></div>
              <div className="flex gap-2 mt-3">
                <button disabled data-testid="confirm-payment-disabled" className="flex-1 py-2 rounded-lg bg-neutral-200 text-neutral-400 text-[12px] font-semibold flex items-center justify-center gap-1 cursor-not-allowed"><CheckCircle2 className="w-4 h-4" /> Confirm Payment</button>
                <button disabled data-testid="reject-payment-disabled" className="flex-1 py-2 rounded-lg bg-neutral-200 text-neutral-400 text-[12px] font-semibold flex items-center justify-center gap-1 cursor-not-allowed"><XCircle className="w-4 h-4" /> Payment Not Received</button>
              </div>
              <div className="h-px bg-fab-primary-lighter my-3" />
              <div className="flex gap-2">
                <button onClick={() => decide('approve')} disabled={deciding} data-testid="approve-term-btn" className="flex-1 py-2.5 rounded-lg bg-fab-wa text-white text-[13px] font-semibold flex items-center justify-center gap-1.5 disabled:opacity-50"><CheckCircle2 className="w-4 h-4" /> Approve {pa.term === 'CREDIT' ? 'Credit' : 'COD'}</button>
                <button onClick={() => decide('reject')} disabled={deciding} data-testid="reject-term-btn" className="flex-1 py-2.5 rounded-lg bg-fab-error text-white text-[13px] font-semibold flex items-center justify-center gap-1.5 disabled:opacity-50"><XCircle className="w-4 h-4" /> Reject {pa.term === 'CREDIT' ? 'Credit' : 'COD'}</button>
              </div>
              <div className="mt-3 text-[10px] text-fab-text-muted leading-snug">You are the final payment authority. FABTEXS AI will only mark this order as PAID after you tap Confirm.</div>
            </div>
          )}

          {!ptPending && order.status === 'PENDING_PAYMENT' && (
            <div className="fab-card p-5">
              <div className="font-display font-bold text-fab-text">Awaiting Payment</div>
              <p className="text-[12px] text-fab-text-secondary mt-1">Simulate buyer uploading a screenshot.</p>
              <button onClick={simUpload} className="mt-3 btn-primary text-[12px] w-full">Simulate Buyer Upload</button>
            </div>
          )}

          {!ptPending && (order.status === 'AWAITING_VERIFICATION' || order.payment?.status === 'SCREENSHOT_UPLOADED') && (
            <div className="fab-card p-5 border-2 border-fab-orange" data-testid="payment-verification-card">
              <div className="font-display font-bold text-fab-orange flex items-center gap-1.5">⚠️ Payment Verification Required</div>
              <div className="text-[11px] text-fab-text-muted mt-1">Buyer: <span className="text-fab-text">{order.buyer_name || order.buyer_phone}</span></div>
              <div className="text-[11px] text-fab-text-muted">Order: <span className="text-fab-text">{order.order_number}</span></div>
              {proofSrc && (
                <img src={proofSrc}
                     alt="Payment screenshot" data-testid="payment-screenshot" className="mt-3 rounded-lg w-full max-h-80 object-contain bg-neutral-50" />
              )}
              {order.payment?.buyer_note && (
                <div className="mt-2 text-[12px] italic text-fab-text-secondary" data-testid="payment-buyer-note">"{order.payment.buyer_note}"</div>
              )}
              <div className="text-[13px] text-fab-text-secondary mt-2">Amount: <strong className="text-fab-text">{fmt(order.total_amount)}</strong></div>
              <div className="flex gap-2 mt-3">
                <button onClick={confirm} data-testid="confirm-payment" className="flex-1 py-2 rounded-lg bg-fab-wa text-white text-[12px] font-semibold flex items-center justify-center gap-1"><CheckCircle2 className="w-4 h-4" /> Confirm Payment</button>
                <button onClick={reject} data-testid="reject-payment" className="flex-1 py-2 rounded-lg bg-fab-error text-white text-[12px] font-semibold flex items-center justify-center gap-1"><XCircle className="w-4 h-4" /> Payment Not Received</button>
              </div>
              <div className="mt-3 text-[10px] text-fab-text-muted leading-snug">You are the final payment authority. FABTEXS AI will only mark this order as PAID after you tap Confirm.</div>
            </div>
          )}

          {canShip && (
            <div className="fab-card p-5" data-testid="ship-card">
              <div className="font-display font-bold text-fab-text flex items-center gap-1.5">🚚 Ship this order</div>
              <div className="text-[12px] text-fab-text-secondary mt-1">Select the courier and enter the AWB / tracking ID. Add a parcel photo (required), then tap "Mark Shipped". The buyer is notified with tracking details.</div>

              <label className="mt-3 block">
                <span className="text-[11px] font-semibold text-fab-text-secondary uppercase tracking-wider">Courier *</span>
                <select
                  value={courier}
                  onChange={(e) => setCourier(e.target.value)}
                  data-testid="ship-courier-select"
                  className="mt-1 w-full text-[13px] border border-fab-primary-lighter rounded px-2 py-2 bg-white text-fab-text"
                >
                  {(couriers.length ? couriers : [{ id: 'Delhivery', name: 'Delhivery' }]).map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </label>

              
{courier === 'Other' && (
  <label className="mt-3 block">
    <span className="text-[11px] font-semibold text-fab-text-secondary uppercase tracking-wider">
      Transporter Name *
    </span>
    <input
      type="text"
      value={customCourier}
      onChange={(e) => setCustomCourier(e.target.value)}
      placeholder="Enter transporter name"
      data-testid="ship-custom-courier-input"
      className="mt-1 w-full text-[13px] border border-fab-primary-lighter rounded px-2 py-2 bg-white text-fab-text"
    />
  </label>
)}

              <label className="mt-3 block">
                <span className="text-[11px] font-semibold text-fab-text-secondary uppercase tracking-wider">AWB / Tracking ID *</span>
                <input
                  type="text"
                  value={tracking}
                  onChange={(e) => setTracking(e.target.value)}
                  placeholder="e.g. AWB123456789"
                  data-testid="ship-tracking-input"
                  className="mt-1 w-full text-[13px] border border-fab-primary-lighter rounded px-2 py-2 bg-white text-fab-text font-mono"
                />
              </label>

              <label className="mt-3 block">
                <span className="text-[11px] font-semibold text-fab-text-secondary uppercase tracking-wider">📦 Parcel / Shipping Image *</span>
                <input
                  type="file"
                  ref={parcelInputRef}
                  accept="image/jpeg,image/png,image/webp"
                  onChange={(e) => setParcelFile(e.target.files?.[0] || null)}
                  data-testid="ship-parcel-input"
                  className="mt-1 w-full text-[12px] text-fab-text-secondary file:mr-2 file:py-1.5 file:px-3 file:rounded file:border file:border-fab-primary-lighter file:bg-white file:text-fab-text file:font-semibold"
                />
                {parcelFile && (
                  <div className="mt-2 relative inline-block">
                    <img src={URL.createObjectURL(parcelFile)} alt="Parcel preview" data-testid="ship-parcel-preview" className="max-h-40 rounded border" />
                    <button
                      type="button"
                      onClick={() => { setParcelFile(null); if (parcelInputRef.current) parcelInputRef.current.value = ''; }}
                      data-testid="ship-parcel-remove"
                      aria-label="Remove parcel image"
                      className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-fab-error text-white flex items-center justify-center shadow"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                    <div className="mt-1 text-[11px] text-fab-primary">{parcelFile.name} ({Math.round(parcelFile.size / 1024)} KB)</div>
                  </div>
                )}
              </label>

              <button
                onClick={ship}
                disabled={!tracking.trim() || !parcelFile || shipping}
                data-testid="ship-submit-btn"
                className="mt-3 btn-primary w-full text-[13px] flex items-center justify-center gap-1.5 disabled:opacity-40"
              >
                <Truck className="w-4 h-4" /> {shipping ? 'Shipping…' : '🚚 Mark Shipped'}
              </button>
            </div>
          )}

          {order.status === 'SHIPPED' && (
            <div className="fab-card p-5">
              <div className="font-display font-bold text-fab-text mb-2">Shipment</div>
              <div className="text-[13px] text-fab-text-secondary"><strong className="text-fab-text">{order.shipment.courier_name}</strong></div>
              <div className="text-[12px] font-mono text-fab-primary">{order.shipment.tracking_id}</div>
              <button onClick={deliver} className="mt-3 btn-wa w-full text-[13px] py-2 rounded-lg font-semibold">Mark Delivered</button>
            </div>
          )}

          {ptApproved && pa.term === 'CREDIT' && pa.credit_due_date && ['SHIPPED', 'DELIVERED'].includes(order.status) && (
            <div className="fab-card p-5 border-2 border-fab-primary-lighter" data-testid="credit-due-card">
              <div className="font-display font-bold text-fab-text flex items-center gap-1.5">💳 Credit Payment</div>
              <div className="mt-2 text-[13px] space-y-1">
                <div className="flex justify-between"><span className="text-fab-text-muted">Buyer</span><span className="text-fab-text font-semibold">{order.buyer_name || order.buyer_phone}</span></div>
                <div className="flex justify-between"><span className="text-fab-text-muted">Order</span><span className="text-fab-text font-mono">{order.order_number}</span></div>
                <div className="flex justify-between"><span className="text-fab-text-muted">Credit days</span><span className="text-fab-text">{pa.credit_days}</span></div>
                <div className="flex justify-between"><span className="text-fab-text-muted">Due date</span><span className="text-fab-text font-semibold" data-testid="credit-due-date">{pa.credit_due_date}</span></div>
                <div className="flex justify-between"><span className="text-fab-text-muted">Outstanding</span><span className="text-fab-text font-bold">{order.payment?.status === 'VERIFIED' ? '₹0' : fmt(order.total_amount)}</span></div>
              </div>
              {order.payment?.status === 'VERIFIED' ? (
                <div className="mt-3 text-[12px] text-fab-wa font-semibold">✓ Paid — no reminder needed</div>
              ) : (
                <button onClick={notifyBuyer} data-testid="notify-buyer-btn" className="mt-3 btn-primary w-full text-[13px] py-2">Notify Buyer</button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
